import { Close, Undo } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip } from "@mui/material";
import ConfirmationModal from "components/ConfirmationModal";
import GenericModal from "components/GenericModal";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import fetchEventRosterForPayrollEditor from "layouts/pages/payroll/actions/fetchEventRosterForPayrollEditor";
import fetchEventRosterForTimecardEditor from "layouts/pages/payroll/actions/fetchEventRosterForTimecardEditor";
import { cloneDeep, isArray } from "lodash";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { CommandManager } from "utils/classes/commandManager";
import { SaveTimecardDataCommand } from "utils/classes/saveTimecardDataCommand";
import useSort from "utils/useSort";
import AddDeductionsModal from "../../../AddDeductionsModal";
import { TimecardEditorPartnerMembersList } from "./TimecardEditorPartnerMembersList";
import PartnerMemberMassUpdateModal from "../PartnerMemberMassUpdateModal";

export const TimecardEditorPartnerMembersModal = ({
  isOpen,
  toggleModal,
  partner,
  eventDay,
  saveRowMutation,
  navigateWithBack,
  currentPeriod,
  isForPayrollEditor,
  editLocked,
  handleConfirmCreateCorrection,
  correctionActive,
}) => {
  const { companyType, currentEvent, currentLoggedUser, userType } = useAppContextController();

  const autosaveQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  const partnerPrimaryPosition = partner?.primaryPosition;
  const [commandManager, setCommandManager] = useState(() => new CommandManager());
  const [selectedRowForDeductions, setSelectedRowForDeductions] = useState(null);
  const [selectedRowForCorrection, setSelectedRowForCorrection] = useState(null);

  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [isMassUpdateModalOpen, setMassUpdateModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const { order, orderBy, toggleSort } = useSort("firstName", "asc");
  const options = { fetchAll: true, order, orderBy };
  const allOptions = {
    ...options,
    eventUrl: currentEvent?.eventUrl,
    filters: {
      type: "Employee",
      partnerSlug: partner?.slug,
    },
  };

  useEffect(() => {
    return () => {
      // Clear all cached data
      queryClient.removeQueries({ queryKey: ["payrollmembers", allOptions] });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: fetchedMembers,
    isLoading,
    refetch: refetchMembers,
  } = useQuery(
    ["payrollmembers", allOptions],
    () =>
      isForPayrollEditor
        ? fetchEventRosterForPayrollEditor(allOptions)
        : fetchEventRosterForTimecardEditor(allOptions),
    {
      disabled: !partner,
      cacheTime: 0,
      staleTime: 0,
    }
  );

  const handleCreateCorrection = (selected) => {
    handleConfirmCreateCorrection(selected).then(() => {
      refetchMembers();
    });
  };

  const stringifiedOptions = JSON.stringify(allOptions);
  useEffect(() => {
    setCommandManager(() => new CommandManager());
  }, [stringifiedOptions]);

  const updateDataCache = (applicantId, newData) => {
    const currentCacheData = queryClient.getQueryData(["payrollmembers", allOptions]);
    const dataCopy = cloneDeep(currentCacheData);
    const dataArray = dataCopy?.data?.data?.map((row) => {
      if (!row?.subRows?.length && row?._id === applicantId)
        return {
          ...row,
          rosterData: {
            ...row?.rosterData,
            ...newData,
          },
        };
      if (!!row?.subRows?.length && newData.punchId) {
        // We couldn't find a matching row in the main list, but the data includes a punchId
        // So this could be one of the subRows of a Shift Job row
        return {
          ...row,
          subRows: row.subRows.map((sr) => {
            if (sr.punchId === newData.punchId) {
              return {
                ...sr,
                rosterData: {
                  ...row?.rosterData,
                  ...newData,
                },
              };
            }
            return sr;
          }),
        };
      }
      return row;
    });
    if (dataCopy?.data?.data) dataCopy.data.data = dataArray;
    queryClient.setQueryData(["payrollmembers", allOptions], dataCopy);
  };

  const pushChange = async (requestData, originalData) => {
    const command = new SaveTimecardDataCommand(
      autosaveQueueRef,
      isProcessingQueueRef,
      requestData,
      originalData,
      updateDataCache,
      saveRowMutation,
      isForPayrollEditor
    );
    commandManager.executeCommand(command);
  };

  const onChangeField = (row, fieldName, fieldValue) => {
    // Find if there's an edit for the current field
    const date = moment(currentEvent?.eventDate).format("MM/DD/YY");
    const editKey = `${row?._id}-${date}-${row?.punchId ?? ""}`;

    const originalData = {
      timeIn: row?.rosterData?.timeIn,
      timeOut: row?.rosterData?.timeOut,
      payRate: row?.rosterData?.payRate,
      billRate: row?.rosterData?.billRate,
      deductionCode: row?.rosterData?.deductionCode,
      deductionAmount: row?.rosterData?.deductionAmount,
      deductionAmountType: row?.rosterData?.deductionAmountType,
      // deductionMaxAmount: row?.rosterData?.deductionMaxAmount,
      // deductionMaxAmountType: row?.rosterData?.deductionMaxAmountType,
      deductionSponsor: row?.rosterData?.deductionSponsor,
      salary: row?.rosterData?.salary ?? "No",
      earningId: row?.rosterData?.earningId,
    };

    const newData = { ...originalData };
    if (isArray(fieldName)) {
      let hasChanges = false;
      for (let index = 0; index < fieldName.length; index += 1) {
        const fieldN = fieldName[index];
        const fieldV = fieldValue[index];

        newData[fieldN] = fieldV;

        // eslint-disable-next-line eqeqeq
        if ((fieldV ?? "") != (originalData[fieldN] ?? "")) hasChanges = true;
      }
      if (!hasChanges) return;
    } else {
      newData[fieldName] = fieldValue;

      // eslint-disable-next-line eqeqeq
      if ((fieldValue ?? "") == (originalData[fieldName] ?? "")) return;
    }

    // eslint-disable-next-line eqeqeq
    if (fieldValue == (originalData[fieldName] ?? "")) return;

    if (currentEvent?.eventUrl)
      pushChange(
        {
          eventUrl: currentEvent?.eventUrl,
          data: {
            applicantId: row?._id,
            timeIn: newData.timeIn,
            timeOut: newData.timeOut,
            payRate: newData.payRate,
            billRate: newData.billRate,
            deductionCode: newData.deductionCode,
            deductionAmount: newData.deductionAmount,
            deductionAmountType: newData.deductionAmountType,
            // deductionMaxAmount: newData.deductionMaxAmount,
            // deductionMaxAmountType: newData.deductionMaxAmountType,
            deductionSponsor: newData.deductionSponsor,
            salary: newData.salary,
            earningId: newData.earningId,
            startDate: currentPeriod?.[0],
            endDate: currentPeriod?.[1],
          },
        },
        originalData
      );
  };

  // Undo function
  const handleUndo = useCallback(() => {
    if (commandManager.undo()) enqueueSnackbar("Last change undone", { variant: "success" });
    else enqueueSnackbar("Nothing to undo", { variant: "error" });
  }, [commandManager, enqueueSnackbar]);

  const handleAddDeduction = (row, deduction) => {
    onChangeField(
      row,
      [
        "deductionCode",
        "deductionAmount",
        "deductionAmountType",
        // "deductionMaxAmount",
        // "deductionMaxAmountType",
        "deductionSponsor",
      ],
      [
        deduction?.code,
        deduction?.amount,
        deduction?.amountType,
        // deduction?.maxAmount,
        // deduction?.maxAmountType,
        deduction?.sponsor,
      ]
    );
  };

  const handleMassUpdateSubmit = (values) => {
    if (!values || !Object.values(values)?.length || !selectedApplicants?.length) {
      enqueueSnackbar("Nothing to update", { variant: "error" });
      return;
    }

    const applicants = fetchedMembers?.data?.data;
    const updatedApplicants = applicants.filter((appl) =>
      selectedApplicants.includes(appl?._id || appl?.id)
    );

    if (!updatedApplicants.length) {
      enqueueSnackbar("Nothing to update", { variant: "error" });
      return;
    }

    updatedApplicants.forEach((appl) => {
      onChangeField(appl, Object.keys(values), Object.values(values));
    });

    enqueueSnackbar("Members updated", { variant: "success" });
  };

  // Keyboard shortcut for UNDO
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if any input field is focused
      // eslint-disable-next-line prefer-destructuring
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.contentEditable === "true" ||
          activeElement.isContentEditable);

      // Only handle shortcuts when no input is focused
      if (!isInputFocused && (event.ctrlKey || event.metaKey)) {
        if (event.key === "z" && !event.shiftKey) {
          event.preventDefault();
          handleUndo();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo]);

  return (
    <>
      <GenericModal
        open={isOpen}
        setOpen={toggleModal}
        overflow="auto"
        width="70vw"
        header={
          <Box
            display="flex"
            sx={{ flex: 1, justifyContent: "space-between", alignItems: "flex-start" }}
          >
            <MDBox sx={{ mt: 2, mb: 1, ml: 1 }}>
              <MDTypography variant="h6" color="dark">
                {`${currentEvent?.venueName} - ${currentEvent?.eventName}`}
              </MDTypography>
              <MDTypography variant="h5" color="dark">
                {`${partner?.name}: Member Attendance`}
              </MDTypography>
            </MDBox>
            <IconButton onClick={toggleModal}>
              <Close />
            </IconButton>
          </Box>
        }
        body={
          <>
            <Box
              height="calc(80vh - 10rem)"
              minHeight="24rem"
              display="flex"
              flexDirection="column"
            >
              <>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    {saveRowMutation.isLoading && (
                      <>
                        <MDTypography variant="text" color="dark" sx={{ mr: 2 }}>
                          Saving
                        </MDTypography>
                        <CircularProgress color="secondary" size={20} sx={{ marginRight: 3 }} />
                      </>
                    )}
                  </Box>
                  <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    <Button
                      variant="contained"
                      onClick={() => setMassUpdateModalOpen(true)}
                      style={{ color: "white" }}
                      disabled={editLocked || !selectedApplicants?.length}
                    >
                      Update Selected
                    </Button>
                    <Tooltip title="Undo">
                      <span>
                        <IconButton
                          color="info"
                          onClick={handleUndo}
                          disabled={isLoading || editLocked}
                        >
                          <Undo fontSize="large" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              </>
              <Box pt={2} display="flex" flex={1}>
                <TimecardEditorPartnerMembersList
                  applicants={fetchedMembers?.data?.data}
                  isLoading={isLoading}
                  onChangeField={onChangeField}
                  eventDay={eventDay}
                  navigateWithBack={navigateWithBack}
                  setSelectedRowForDeductions={setSelectedRowForDeductions}
                  isForPayrollEditor={isForPayrollEditor}
                  editLocked={editLocked}
                  onSelectRowForCorrection={setSelectedRowForCorrection}
                  correctionActive={correctionActive}
                  selectedApplicants={selectedApplicants}
                  setSelectedApplicants={setSelectedApplicants}
                />
              </Box>
            </Box>
            {!!selectedRowForDeductions && (
              <AddDeductionsModal
                open={!!selectedRowForDeductions}
                setOpen={() => {
                  setSelectedRowForDeductions(null);
                }}
                onAddDeduction={handleAddDeduction}
                row={selectedRowForDeductions}
              />
            )}
            {!!selectedRowForCorrection && (
              <ConfirmationModal
                isOpen={!!selectedRowForCorrection}
                onClose={() => {
                  setSelectedRowForCorrection(null);
                }}
                title="Create correction"
                body="Are you sure you want to create a correction for this row?"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleCreateCorrection(selectedRowForCorrection);
                  setSelectedRowForCorrection(null);
                }}
              />
            )}
            {isMassUpdateModalOpen && (
              <PartnerMemberMassUpdateModal
                open={isMassUpdateModalOpen}
                setOpen={() => {
                  setMassUpdateModalOpen(false);
                }}
                onUpdate={handleMassUpdateSubmit}
                eventDate={currentEvent?.eventDate}
                isForPayrollEditor={isForPayrollEditor}
              />
            )}
          </>
        }
      />
    </>
  );
};
